import { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LibraryMusicOutlinedIcon from '@mui/icons-material/LibraryMusicOutlined';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import CustomMenu from '../common/CustomMenu';
import theme from '../../theme';

const parseReleaseDate = (releaseDate) => {
  if (!releaseDate || typeof releaseDate !== 'string') return null;
  const parts = releaseDate
    .trim()
    .split(/[.-]/)
    .map((part) => Number(part));

  if (parts.length < 3 || parts.some((part) => !Number.isFinite(part))) return null;

  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
};

const isWithinLatestAlbumWindow = (releaseDate) => {
  const parsedReleaseDate = parseReleaseDate(releaseDate);
  if (!parsedReleaseDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

  return parsedReleaseDate >= monthAgo;
};

const LatestAlbumManagementButton = ({
  albumInfo,
  onMoveRegion,
  onHide,
  onRegister,
  onRemoveRecommended,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!albumInfo) return null;

  const isDomestic = albumInfo.displayRegion === 'KR';
  const targetRegion = isDomestic ? 'GLOBAL' : 'KR';
  const moveText = isDomestic ? '최신 앨범 해외로 이동' : '최신 앨범 국내로 이동';
  const hasPersistedAlbumId = Number.isFinite(Number(albumInfo.id));
  const isAppleMusicAlbum = albumInfo.albumSource?.toUpperCase() === 'APPLE_MUSIC';
  const isInLatestWindow = isWithinLatestAlbumWindow(albumInfo.releaseDate);
  const isHiddenFromLatest = albumInfo.newReleaseOverride === 'EXCLUDE';
  const canManageRegisteredLatestAlbum =
    hasPersistedAlbumId && isInLatestWindow && !isHiddenFromLatest;
  const canRegisterLatestAlbum =
    isAppleMusicAlbum && isInLatestWindow && !canManageRegisteredLatestAlbum;
  const canOpenMenu = canManageRegisteredLatestAlbum || canRegisterLatestAlbum;
  const tooltipTitle = canOpenMenu
    ? '최신 앨범 관리'
    : !isInLatestWindow
      ? '최근 한달 이내 발매 앨범만 최신 앨범으로 관리할 수 있습니다'
      : '저장된 앨범만 최신 앨범으로 관리할 수 있습니다';

  const closeMenu = () => setAnchorEl(null);
  const handleOpen = (event) => {
    if (!canOpenMenu) return;
    setAnchorEl(event.currentTarget);
  };

  const menuItems = [
    ...(canRegisterLatestAlbum
      ? [
          {
            text: '최신 앨범에 등록',
            onClick: () => {
              onRegister?.();
              closeMenu();
            },
            iconNode: <LibraryAddOutlinedIcon />,
          },
        ]
      : []),
    ...(canManageRegisteredLatestAlbum
      ? [
          {
            text: moveText,
            onClick: () => {
              onMoveRegion?.(targetRegion);
              closeMenu();
            },
            iconNode: <SwapHorizOutlinedIcon />,
          },
          {
            text: '최신 앨범에서 숨김 처리',
            onClick: () => {
              onHide?.();
              closeMenu();
            },
            iconNode: <VisibilityOffOutlinedIcon />,
          },
        ]
      : []),
    ...(canManageRegisteredLatestAlbum && albumInfo.isRecommended
      ? [
          {
            text: '포스트팝 추천에서 삭제',
            onClick: () => {
              onRemoveRecommended?.();
              closeMenu();
            },
            iconNode: <DeleteOutlineOutlinedIcon />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <IconButton
            disableRipple
            aria-label="최신 앨범 관리"
            disabled={!canOpenMenu}
            onClick={handleOpen}
            sx={{
              width: { xs: 32, md: 40 },
              height: { xs: 32, md: 40 },
              border: `1px solid ${theme.palette.grey.line}`,
              borderRadius: 1,
              color: theme.palette.black[4],
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                color: theme.palette.grey[300],
              },
            }}
          >
            <LibraryMusicOutlinedIcon
              sx={{
                fontSize: { xs: 19, md: 24 },
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <CustomMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        items={menuItems}
      />
    </>
  );
};

export default LatestAlbumManagementButton;
